import { InfoCircleOutlined, SearchOutlined } from '@ant-design/icons';
import getPathValue from '@rc-component/util/es/utils/get';
import { Button, DatePicker, Input, InputRef, Popover, Space } from 'antd';
import { RangePickerProps } from "antd/es/date-picker";
import { FilterConfirmProps, FilterDropdownProps } from "antd/es/table/interface";
import dayjs, { Dayjs } from "dayjs";
import { useState } from 'react';
import './Grid.less';
import { FilterProps, FilterType, IGridEntry } from "./gridTypes";

interface Props<RecordType> extends FilterDropdownProps {
  data: readonly (RecordType & IGridEntry)[],
  filter: FilterProps,
  inputRef: React.LegacyRef<InputRef>,
  onSearch: (selectedKeys: string[], confirm: (param?: FilterConfirmProps) => void, dataIndex: unknown, type: FilterType, isRegex: boolean, isCaseSensitive: boolean) => void;
  onReset: (clearFilters: () => void, confirm: (param?: FilterConfirmProps) => void) => void;
}

function XmFilterDropdown<RecordType>({
  setSelectedKeys,
  selectedKeys,
  confirm,
  clearFilters,
  filter,
  onSearch,
  onReset,
  inputRef,
  data
}: Props<RecordType>) {
  const [isRegex, setIsRegex] = useState(false);
  const [isCaseSensitive, setIsCaseSensitive] = useState(false);
  const toggleRegex = () => setIsRegex(prev => {
    if (!prev)
      setIsCaseSensitive(false);

    return !prev;
  });

  const toggleCaseSensitive = () => setIsCaseSensitive(prev => {
    if (!prev)
      setIsRegex(false);

    return !prev;
  });

  const { min, max } = getRange(data, filter);
  const disabledTime: RangePickerProps['disabledTime'] = (current) => {
    const clone = current?.clone();

    return {
      disabledHours: () => {
        const hours: number[] = [];
        if (clone.endOf('day').isSame(min().endOf('day'))) {
          for (let i = 0; i < min().hour(); i++)
            hours.push(i);
        }

        if (clone.endOf('day').isSame(max().endOf('day'))) {
          for (let i = max().hour() + 1; i < 24; i++)
            hours.push(i);
        }

        return hours;
      },
      disabledMinutes: hour => {
        const minutes: number[] = [];
        if (clone.endOf('day').isSame(min().endOf('day')) && hour === min().hour()) {
          for (let i = 0; i < min().minute(); i++)
            minutes.push(i);
        }

        if (clone.endOf('day').isSame(max().endOf('day')) && hour === max().hour()) {
          for (let i = max().minute() + 1; i < 60; i++)
            minutes.push(i);
        }

        return minutes;
      }
    };
  };

  function disabledDate(current: Dayjs) {
    const m = current?.clone();
    return !current || m.endOf('day').isBefore(min()) || m.startOf('day').isAfter(max());
  }

  return (
    <div style={{ padding: 8 }}>
      {filter.type === 'timestamp' ?
        <DatePicker.RangePicker className="date-filter-input" disabledDate={disabledDate}
          disabledTime={disabledTime} showTime={{ hideDisabledOptions: true }}
          onChange={dates => dates && setSelectedKeys(dates.map(m => m.unix()))} presets={[
            { label: 'MinMax', value: [min(), max()] }
          ]}
          defaultValue={[min(), max()]}
        /> :
        <Input
          className={filter.type == 'string' ? 'xm-filter' : ''}
          ref={inputRef}
          placeholder={(filter.type === 'string' ? 'Search ' : 'Filter ') + 'data'}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => onSearch(selectedKeys as string[], confirm, filter.field, filter.type, isRegex, isCaseSensitive)}
          style={{ width: 188, marginBottom: 8 }}
          suffix={filter.type == 'number' ?
            <SuffixNumber /> :
            filter.type == 'string' ?
              <SuffixString
                isRegex={isRegex}
                onClickRegex={toggleRegex}
                isCaseSensitive={isCaseSensitive}
                onClickCaseSensitive={toggleCaseSensitive}
              /> : null
          }
        />}
      <br />
      <Space>
        <Button onClick={() => onReset(clearFilters, confirm)} size="small" style={{ width: 90 }}>
          Reset
        </Button>
        <Button
          type="primary"
          icon={<SearchOutlined />}
          size="small"
          style={{ width: 90 }}
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            onSearch(selectedKeys as string[], confirm, filter.field, filter.type, isRegex, isCaseSensitive);
          }}
        >
          {filter.type === 'string' ? 'Search' : 'Filter'}
        </Button>
      </Space>
    </div>
  );
}

interface SuffixStringProps {
  isRegex: boolean;
  isCaseSensitive: boolean;
  onClickRegex: () => void;
  onClickCaseSensitive: () => void;
}

function SuffixString({ isRegex, onClickRegex, isCaseSensitive, onClickCaseSensitive }: SuffixStringProps) {
  return <div className='xm-suffix-string'>
    <Button type='text' onClick={onClickCaseSensitive} className={isCaseSensitive ? `xm-case-sensitive-btn btn active` : `xm-case-sensitive-btn btn`}>Aa</Button>
    <Button type='text' onClick={onClickRegex} className={isRegex ? `xm-regex-btn btn active` : `xm-regex-btn btn`}>.*</Button>
  </div>;
}

const SuffixNumber = () => (
  <Popover content={filterHelp}>
    <InfoCircleOutlined />
  </Popover>
);

function getRange<T extends object>(data: readonly T[], { type, field }: FilterProps) {
  let minUnix = Number.MAX_SAFE_INTEGER;
  let maxUnix = 0;
  if (data && type === 'timestamp') {
    data.forEach(row => {
      const timestamp: number = getPathValue(row, field);
      if (minUnix > timestamp)
        minUnix = timestamp;

      if (maxUnix < timestamp)
        maxUnix = timestamp;
    });
  }

  const minMoment = dayjs.unix(minUnix);
  const maxMoment = dayjs.unix(maxUnix);

  return {
    min: () => minMoment.clone(),
    max: () => maxMoment.clone()
  };
};

const filterHelp = (
  <table className="filter-help">
    <tbody>
      <tr>
        <td>
          <code>123</code>
        </td>
        <td>display strings containing 123</td>
      </tr>
      <tr>
        <td>
          <code>&lt;123</code>
        </td>
        <td>display values lower than 123</td>
      </tr>
      <tr>
        <td>&gt;123</td>
        <td>display values greater than 123</td>
      </tr>
      <tr>
        <td>
          <code>=123</code>
        </td>
        <td>display values equal to 123</td>
      </tr>
      <tr>
        <td>
          <code>12-34</code>
        </td>
        <td>display values between 12 and 34</td>
      </tr>
    </tbody>
  </table>
);

export default XmFilterDropdown;